/**
 * @file helper.method.ts
 * @module utils/helper.method
 * @description Shared utility helpers for validation and settings API fetch.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import APICallService from "../api/apiCallService";
import { platformSettings } from "../api/apiEndPoints";
import { Settings as SettingsPermission, StockUpdateReason } from "./constants";
import { getKey } from "../Global/history";
import { warehouse as warehouseStore } from "./storeString";
import { IConfigurationRecord } from "../types/common/common.types";

/**
 * Validates IPv4 address format, with an optional ":port" suffix for printers
 * listening on a non-default port (e.g. the dev virtual printer on :8000).
 * @param {string} value Candidate IP string.
 * @returns {boolean} True when value is a valid IPv4 address.
 */
export const isValidIp = (value: string): boolean =>
  /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}(:\d{1,5})?$/.test(
    value.trim(),
  );

/**
 * Validates network port range.
 * @param {string} value Candidate port value.
 * @returns {boolean} True when value is an integer between 1 and 65535.
 */
export const isValidPort = (value: string): boolean => {
  const n = Number(value.trim());
  return Number.isInteger(n) && n >= 1 && n <= 65535;
};

/**
 * Resolves active warehouse ID from override or localStorage.
 * @param {string} [override] Optional warehouse override.
 * @returns {string} Warehouse ID or empty string for global scope.
 */
const getSelectedWarehouse = (override?: string): string => {
  if (override) return override;
  const stored = getKey(warehouseStore.warehouseRef);
  if (!stored) return "";
  try {
    const parsed = JSON.parse(String(stored));
    return parsed?._id || parsed?.value || "";
  } catch {
    return "";
  }
};

/**
 * Extracts first record from API response.
 * @param {any} response API response object.
 * @returns {IConfigurationRecord | undefined} First record or undefined.
 */
const parseSettingRecord = (response: any): IConfigurationRecord | undefined => {
  if (!response) return undefined;
  return Array.isArray(response?.record) ? response.record[0] : response?.record;
};

/**
 * Calls settings single-key API and returns one configuration setting payload.
 * Prefers the active warehouse scope when available so warehouse-scoped values
 * resolve correctly against the v2 API contract.
 * @param {string} settingKey Setting key.
 * @param {string} [warehouseId] Optional warehouse override.
 * @returns {Promise<IConfigurationRecord | undefined>} Setting payload.
 */
export const fetchConfigurationSettingByKey = async (
  settingKey: string,
  warehouseId?: string,
): Promise<IConfigurationRecord | undefined> => {
  const selectedWarehouse = getSelectedWarehouse(warehouseId);
  const apiService = new APICallService(
    platformSettings.getConfigurationSetting,
    settingKey,
    "",
    "",
    "",
    false,
    SettingsPermission,
    selectedWarehouse,
  );
  const response = await apiService.callAPI();
  return parseSettingRecord(response);
};

/**
 * Validates and normalizes a bool-like setting value.
 * @param {*} value Incoming setting value.
 * @param {boolean} fallback Fallback value for invalid input.
 * @returns {boolean} Normalized boolean value.
 */
export const getValidatedBooleanSettingValue = (
  value: any,
  fallback = false,
): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (["true", "yes", "1"].includes(normalized)) {
    return true;
  }
  if (["false", "no", "0"].includes(normalized)) {
    return false;
  }

  return fallback;
};

/** method used for getting custom name of `Stock updated reasons` */
export const getStockUpdateReasonLabel = (reason: StockUpdateReason) => {
  let val = "";
  switch (reason) {
    case StockUpdateReason.MissingInventory:
      val = "Insufficient Stock in Batch/Inventory";
      break;

    default:
      val = reason;
      break;
  }
  return val;
};
